import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp as StatsIcon,
  People as ClientsIcon,
  AttachMoney as MoneyIcon,
  CalendarMonth as BookingIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import Header from "../components/Header";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface StatCardData {
  id: string;
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
}

interface RecentBooking {
  id: number;
  client_name: string;
  master_name: string;
  service_name: string;
  start_time: string;
  status: string;
  price: number;
}

interface DayPoint {
  date: string;
  count: number;
  revenue: number;
}

interface DashboardData {
  scope: "salon" | "master";
  bookings_today: number;
  completed_today: number;
  revenue_today: number;
  revenue_week: number;
  clients_total: number;
  clients_new_week: number;
  occupancy_today: number;
  occupancy_week: number;
  recent_bookings: RecentBooking[];
  bookings_by_day: DayPoint[];
}

const STATUS_LABELS: Record<string, string> = {
  pending: "ожидает",
  confirmed: "подтверждена",
  completed: "завершена",
  cancelled: "отменена",
  no_show: "неявка",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "#FF9800",
  confirmed: "#2196F3",
  completed: "#4CAF50",
  cancelled: "#9E9E9E",
  no_show: "#F44336",
};

const rub = (v: number) => `${Math.round(v).toLocaleString("ru-RU")} ₽`;

const BASE_CARDS: StatCardData[] = [
  { id: "bookings", title: "Записи сегодня", value: "—", subtitle: "", icon: <BookingIcon />, color: "#2196F3" },
  { id: "clients", title: "Клиенты", value: "—", subtitle: "", icon: <ClientsIcon />, color: "#4CAF50" },
  { id: "revenue", title: "Выручка сегодня", value: "—", subtitle: "", icon: <MoneyIcon />, color: "#FF9800" },
  { id: "occupancy", title: "Загруженность", value: "—", subtitle: "", icon: <StatsIcon />, color: "#9C27B0" },
];

interface StatCardProps {
  card: StatCardData;
  provided: any;
  snapshot: any;
}

const StatCard: React.FC<StatCardProps> = ({ card, provided, snapshot }) => {
  return (
    <Card
      ref={provided.innerRef}
      {...provided.draggableProps}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        bgcolor: snapshot.isDragging ? "action.hover" : "background.paper",
        transform: snapshot.isDragging ? "rotate(5deg)" : "none",
        transition: "box-shadow 0.2s ease",
        boxShadow: snapshot.isDragging ? 8 : 1,
        "&:hover": {
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" flex={1}>
            <Avatar sx={{ bgcolor: card.color, width: 48, height: 48, mr: 2 }}>
              {card.icon}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={300} color="text.primary">
                {card.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.title}
              </Typography>
            </Box>
          </Box>
          <Box {...provided.dragHandleProps} sx={{ cursor: "grab", p: 1, "&:active": { cursor: "grabbing" } }}>
            <DragIcon sx={{ color: "text.secondary" }} />
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {card.subtitle}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default function DashboardPage() {
  const [statCards, setStatCards] = useState<StatCardData[]>(BASE_CARDS);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Загружаем порядок карточек из localStorage при монтировании
  useEffect(() => {
    const savedOrder = localStorage.getItem("dashboardCardsOrder");
    if (savedOrder) {
      try {
        const order = JSON.parse(savedOrder);
        const reorderedCards = order
          .map((cardId: string) => BASE_CARDS.find((card) => card.id === cardId))
          .filter(Boolean) as StatCardData[];
        if (reorderedCards.length === BASE_CARDS.length) {
          setStatCards(reorderedCards);
        }
      } catch (e) {
        console.error("Error loading dashboard cards order:", e);
      }
    }
  }, []);

  // Живые метрики
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
          now.getDate()
        ).padStart(2, "0")}`;
        const resp = await fetch(`${API_BASE}/analytics/dashboard?day=${day}`, {
          headers: getAuthHeaders(),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const d: DashboardData = await resp.json();
        setData(d);
        const fill: Record<string, { value: string; subtitle: string }> = {
          bookings: {
            value: String(d.bookings_today),
            subtitle: `${d.completed_today} завершено`,
          },
          clients: {
            value: String(d.clients_total),
            subtitle: `+${d.clients_new_week} за неделю`,
          },
          revenue: {
            value: rub(d.revenue_today),
            subtitle: `за 7 дней: ${rub(d.revenue_week)}`,
          },
          occupancy: {
            value: `${d.occupancy_today}%`,
            subtitle: `в среднем за неделю: ${d.occupancy_week}%`,
          },
        };
        setStatCards((prev) =>
          prev.map((card) => (fill[card.id] ? { ...card, ...fill[card.id] } : card))
        );
      } catch (e) {
        console.error("Dashboard load error:", e);
        setError("Не удалось загрузить статистику");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  // Сохраняем порядок карточек в localStorage при изменении
  useEffect(() => {
    if (statCards.length > 0) {
      const order = statCards.map((card) => card.id);
      localStorage.setItem("dashboardCardsOrder", JSON.stringify(order));
    }
  }, [statCards]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(statCards);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setStatCards(items);
  };

  const chart = data?.bookings_by_day || [];
  const maxCount = Math.max(1, ...chart.map((p) => p.count));

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Header title="Статистика" />

      <Box sx={{ p: 4 }}>
        {/* Заголовок */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 300, color: "text.primary" }}>
            Статистика салона
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            {data?.scope === "master" ? "Ваши показатели" : "Аналитика и показатели эффективности"}
          </Typography>
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>

        {/* Карточки статистики с react-beautiful-dnd */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="dashboard-cards" direction="horizontal">
            {(provided) => (
              <Grid
                container
                spacing={3}
                sx={{ mb: 4 }}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {statCards.map((card, index) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Draggable draggableId={card.id} index={index}>
                      {(provided, snapshot) => (
                        <StatCard
                          card={card}
                          provided={provided}
                          snapshot={snapshot}
                        />
                      )}
                    </Draggable>
                  </Grid>
                ))}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </DragDropContext>

        {/* График и последние записи */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2, height: 400 }}>
              <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: "text.primary" }}>
                  Записи за 14 дней
                </Typography>
                {loading ? (
                  <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <Box sx={{ flexGrow: 1, display: "flex", alignItems: "flex-end", gap: 1, pt: 2 }}>
                    {chart.map((p) => {
                      const d = new Date(p.date + "T00:00:00");
                      const label = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
                      return (
                        <Tooltip
                          key={p.date}
                          title={`${label}: ${p.count} записей, выручка ${rub(p.revenue)}`}
                          arrow
                        >
                          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                              {p.count > 0 ? p.count : ""}
                            </Typography>
                            <Box
                              sx={{
                                width: "100%",
                                maxWidth: 28,
                                height: `${Math.max(2, (p.count / maxCount) * 75)}%`,
                                bgcolor: p.count > 0 ? "#2196F3" : "#e0e0e0",
                                borderRadius: "4px 4px 0 0",
                                transition: "height 0.3s ease",
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: 10 }}>
                              {label}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2, height: 400 }}>
              <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: "text.primary" }}>
                  Последние записи
                </Typography>
                <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                  {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (data?.recent_bookings || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                      Записей пока нет
                    </Typography>
                  ) : (
                    (data?.recent_bookings || []).map((b, index, arr) => {
                      const d = new Date(b.start_time);
                      const time = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                      return (
                        <Box
                          key={b.id}
                          sx={{
                            py: 1.5,
                            borderBottom: index < arr.length - 1 ? "1px solid #f0f0f0" : "none",
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" fontWeight={500}>
                              {b.client_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: STATUS_COLORS[b.status] || "text.secondary", fontWeight: 600 }}
                            >
                              {STATUS_LABELS[b.status] || b.status}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {b.service_name} • {time} • {b.master_name}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
